import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Users, Heart, BookOpen, Music, Utensils, Palette } from 'lucide-react';

const LivingCultures = () => {
 return (
 <div className="living-cultures-page">
 <section className="page-hero african-accent-border">
 <div className="section-container">
 <h1 className="page-title">Living Cultures Programme</h1>
 <p className="page-subtitle">
 Culture is not taught. It is lived.
 </p>
 </div>
 </section>

 <section className="intent-section">
 <div className="section-container">
 <div className="intent-box">
 <h2>Our Intent</h2>
 <p>
 Nova Roots treats Kenyan culture as living knowledge, not curriculum decoration. Cultural learning is embedded into the core experience of the school, not delivered through occasional theme days or performances.
 </p>
 <p>
 Each week, a different Kenyan community is invited into the school to teach directly. Elders, artists and craftspeople become educators, sharing knowledge that is lived, practised and passed down, not extracted or displayed.
 </p>
 <p className="intent-emphasis">
 This programme exists to restore dignity to culture, honour knowledge systems outside textbooks, and ensure children grow up grounded in respect, curiosity and belonging.
 </p>
 </div>
 </div>
 </section>

 <section className="practice-section">
 <div className="section-container">
 <h2 className="section-title">What This Looks Like in Practice</h2>
 <p className="practice-intro">
 This is participatory learning, not observation. Children engage with people, not presentations.
 </p>
 <div className="practice-grid">
 <Card className="practice-card">
 <CardContent className="practice-content">
 <div className="practice-icon">
 <Users size={28} />
 </div>
 <h3>Weekly Immersion</h3>
 <p>A different Kenyan community visits each week</p>
 </CardContent>
 </Card>

 <Card className="practice-card">
 <CardContent className="practice-content">
 <div className="practice-icon">
 <BookOpen size={28} />
 </div>
 <h3>Storytelling & Oral History</h3>
 <p>Origin narratives and lived wisdom passed down</p>
 </CardContent>
 </Card>

 <Card className="practice-card">
 <CardContent className="practice-content">
 <div className="practice-icon">
 <Music size={28} />
 </div>
 <h3>Music, Rhythm & Dance</h3>
 <p>Traditional and contemporary cultural expressions</p>
 </CardContent>
 </Card>

 <Card className="practice-card">
 <CardContent className="practice-content">
 <div className="practice-icon">
 <Utensils size={28} />
 </div>
 <h3>Food & Shared Meals</h3>
 <p>Preparation with meaning, context and community</p>
 </CardContent>
 </Card>

 <Card className="practice-card">
 <CardContent className="practice-content">
 <div className="practice-icon">
 <Palette size={28} />
 </div>
 <h3>Hands-On Craft</h3>
 <p>Skill-making guided by practitioners</p>
 </CardContent>
 </Card>

 <Card className="practice-card">
 <CardContent className="practice-content">
 <div className="practice-icon">
 <Heart size={28} />
 </div>
 <h3>Open Dialogue</h3>
 <p>Children ask questions and listen deeply</p>
 </CardContent>
 </Card>
 </div>
 </div>
 </section>

 <section className="structure-section">
 <div className="section-container">
 <h2 className="section-title">Educational Structure</h2>
 <p className="structure-intro">
 Each cultural engagement follows a clear learning arc:
 </p>
 <div className="learning-arc">
 <Card className="arc-card before-card">
 <CardContent className="arc-content">
 <h3>Before</h3>
 <p>Children are prepared through:</p>
 <ul>
 <li>Context setting</li>
 <li>Geography</li>
 <li>Language roots</li>
 <li>Guiding questions</li>
 <li>Respectful listening expectations</li>
 </ul>
 </CardContent>
 </Card>

 <Card className="arc-card during-card">
 <CardContent className="arc-content">
 <h3>During</h3>
 <p>
 Visits are interactive and guided. Contributors lead learning in their own voice and on their own terms.
 </p>
 </CardContent>
 </Card>

 <Card className="arc-card after-card">
 <CardContent className="arc-content">
 <h3>After</h3>
 <p>Children reflect through:</p>
 <ul>
 <li>Discussion</li>
 <li>Journalling</li>
 <li>Creative response</li>
 <li>Applied projects connected to leadership, land, identity and community</li>
 </ul>
 <p className="assessment-note">
 <strong>Culture is not assessed.</strong> Learning responses are observed and documented.
 </p>
 </CardContent>
 </Card>
 </div>
 </div>
 </section>

 <section className="ethics-section">
 <div className="section-container">
 <h2 className="section-title">Ethics & Safeguarding</h2>
 <p className="ethics-intro">
 Nova Roots operates this programme through a clear ethical framework:
 </p>
 <div className="ethics-grid">
 <div className="ethics-item">
 <div className="ethics-check">✓</div>
 <p>All contributors are paid professionals</p>
 </div>
 <div className="ethics-item">
 <div className="ethics-check">✓</div>
 <p>Communities retain authority over their stories and practices</p>
 </div>
 <div className="ethics-item">
 <div className="ethics-check">✓</div>
 <p>No performance, costume play or cultural tokenism</p>
 </div>
 <div className="ethics-item">
 <div className="ethics-check">✓</div>
 <p>No extraction without consent, context or continuity</p>
 </div>
 </div>
 <p className="ethics-emphasis">
 Dignity is non-negotiable.
 </p>
 </div>
 </section>

 <section className="why-matters-section">
 <div className="section-container">
 <h2 className="section-title">Why This Matters</h2>
 <div className="why-content">
 <p className="why-intro">
 Children learn that culture is not static, exotic or historical. It is alive, evolving and worthy of respect. They grow up understanding Kenya not as a single story, but as a rich ecosystem of peoples, knowledge systems and ways of living.
 </p>
 <div className="builds-grid">
 <h3>This Builds:</h3>
 <div className="builds-items">
 <div className="build-item">Empathy and humility</div>
 <div className="build-item">Strong identity and belonging</div>
 <div className="build-item">Respect for multiple forms of intelligence</div>
 <div className="build-item">Leadership grounded in understanding, not dominance</div>
 </div>
 </div>
 </div>
 </div>
 </section>

 <section className="scalability-section">
 <div className="section-container">
 <h2 className="section-title">Scalability</h2>
 <div className="scalability-content">
 <p>
 This is a core pillar across all Nova Roots campuses.
 </p>
 <p>
 The framework, ethics and structure are shared. The relationships are local.
 </p>
 <p>
 Each campus partners with communities relevant to its geography, ensuring authenticity whilst maintaining consistency in values and quality.
 </p>
 <p className="scalability-emphasis">
 What scales is the model. What remains rooted is the culture.
 </p>
 </div>
 </div>
 </section>

 <section className="positioning-section">
 <div className="section-container">
 <div className="positioning-box">
 <h2>At Nova Roots, culture is not taught. It is lived.</h2>
 <p>
 Our schools are places where knowledge flows from many voices, and children learn to listen before they lead.
 </p>
 </div>
 </div>
 </section>
 </div>
 );
};

export default LivingCultures;
